import {
  createContextMutationPrecondition,
  parseContextSession,
  type ContextSessionResponse,
  type ControlWebClient,
  type RequestOptions,
} from "@kontextwerk/control-web-client"
import type { AvailabilityPolicyConfig, AvailabilityPolicyHolidayListResponse } from "@kontextwerk/control-web-client"
import {
    normalizeAvailabilityPolicyConfig,
    validateAvailabilityPolicyConfig,
} from "@kontextwerk/control-web-frame/shared/logic/availability-policies"
import {
  briefing,
  holidayYears,
  holidays,
  invalid,
  membershipSettings,
  record,
  uuid,
  type DailyBriefingPreferences,
} from "./_logic/contracts"
import { get } from "svelte/store"
import {
    captureControlContext,
    controlContext,
    rejectControlContext,
} from "@kontextwerk/control-web-frame/shared/logic/authentication"
import { getControlWebClient } from "../client"
import { admitsMembershipSettings } from "./admission"
import type { MembershipSettings } from "./_logic/contracts"
export { admitsMembershipSettings, membershipSettingsRequirements } from "./admission"
export type { MembershipSettings, DailyBriefingPreferences } from "./_logic/contracts"

type Binding = Required<Pick<RequestOptions, "signal" | "isCurrent" | "mutationContext">>

/** Captures the initiating browser context once; no legacy user-ID endpoint or write retry. */
export const createMembershipSettingsApi = (
  client: ControlWebClient,
  value: ContextSessionResponse,
  capture: Binding,
) => {
  const session = parseContextSession(value)
  const pair = createContextMutationPrecondition(capture.mutationContext)

  if (
    session.product_issuer !== "kehrblatt-control" ||
    session.context.kind !== "organization" ||
    session.session_id !== pair.expected_session_id ||
    session.context_revision !== pair.expected_context_revision
  )
    return invalid()

  const organizationId = session.context.organization_id
  const path = `/admin/organizations/${organizationId}/memberships`
  const options = { ...capture, mutationContext: pair, retry: "never" as const }
  const current = (signal: AbortSignal) => {
    if (capture.signal.aborted || signal.aborted || !capture.isCurrent())
      throw new DOMException("Obsolete membership context", "AbortError")
  }
  const request = async (
    method: "GET" | "PUT",
    route: string,
    signal: AbortSignal,
    json?: unknown,
  ) => {
    current(signal)
    const result = await client.transport.request<unknown>(method, route, {
      ...options,
      signal: AbortSignal.any([capture.signal, signal]),
      ...(json === undefined ? {} : { json, requireCsrf: true }),
    })
    current(signal)

    return result
  }

  return {
    async list(after: string | null, signal: AbortSignal) {
      const query = new URLSearchParams({ limit: "100" })

      if (after) query.set("after_membership_id", uuid(after))

      const input = record(await request("GET", `${path}/settings?${query}`, signal))

      if (!Array.isArray(input.items) || input.items.length > 100) return invalid()

      const items = input.items.map((item) => membershipSettings(item, organizationId))
      const next =
        input.next_after_membership_id === null ? null : uuid(input.next_after_membership_id)

      if (
        new Set(items.map((item) => item.membership_id)).size !== items.length ||
        (next !== null && (next === after || next !== items.at(-1)?.membership_id))
      )
        return invalid()

      return { items, next_after_membership_id: next }
    },
    async load(membershipId: string, signal: AbortSignal) {
      const result = membershipSettings(
        await request("GET", `${path}/${uuid(membershipId)}/settings`, signal),
        organizationId,
      )

      if (result.membership_id !== membershipId) return invalid()

      return result
    },
    async saveBriefing(membershipId: string, draft: DailyBriefingPreferences, signal: AbortSignal) {
      const result = membershipSettings(
        await request("PUT", `${path}/${uuid(membershipId)}/settings`, signal, {
          daily_briefing: briefing(draft),
        }),
        organizationId,
      )

      if (result.membership_id !== membershipId) return invalid()

      return result
    },
    async saveWorkingHours(
      membershipId: string,
      draft: AvailabilityPolicyConfig,
      signal: AbortSignal,
    ) {
      const config = normalizeAvailabilityPolicyConfig(draft)

      if (validateAvailabilityPolicyConfig(config)) return invalid()

      const result = membershipSettings(
        await request("PUT", `${path}/${uuid(membershipId)}/working-hours`, signal, { config }),
        organizationId,
      )

      if (result.membership_id !== membershipId || !result.settings.availability_policy)
        return invalid()

      return result
    },
    async loadHolidays(membershipId: string, years: readonly number[], signal: AbortSignal) {
      const requestedYears = holidayYears(years)
      const member = uuid(membershipId)
      const items: AvailabilityPolicyHolidayListResponse["items"] = []
      // The purpose-bound backend accepts at most eight years per read.
      for (let start = 0; start < requestedYears.length; start += 8) {
        const batch = requestedYears.slice(start, start + 8)
        const query = new URLSearchParams()
        batch.forEach((year) => query.append("years", String(year)))
        const result = holidays(
          await request("GET", `${path}/${member}/working-hours/holidays?${query}`, signal),
          batch,
        )
        items.push(...result.items)
      }

      return { items }
    },
  }
}

/** One immutable browser context for product settings, never a legacy identity view. */
export const captureMembershipSettings = (organizationId: string) => {
  const context = get(controlContext)

  if (
    !context ||
    context.session.product_issuer !== "kehrblatt-control" ||
    context.session.password_change_required ||
    context.session.context.kind !== "organization" ||
    context.organizationId !== organizationId
  )
    throw new DOMException("Unconfirmed membership settings context", "AbortError")

  const capture = captureControlContext()

  if (!capture) throw new DOMException("Unconfirmed membership settings context", "AbortError")

  const session = context.session
  const current = () => !capture.signal.aborted && capture.isCurrent()
  const api = admitsMembershipSettings(session, "list")
    ? createMembershipSettingsApi(getControlWebClient(), session, capture)
    : null

  return {
    api,
    session,
    signal: capture.signal,
    current,
    canManage: (membershipId: string) =>
      current() && admitsMembershipSettings(session, "update", membershipId),
  }
}
export type CapturedMembershipSettings = ReturnType<typeof captureMembershipSettings>

export const listCapturedMembershipSettings = async (
  binding: CapturedMembershipSettings,
  signal: AbortSignal,
): Promise<MembershipSettings[]> => {
  if (!binding.current() || signal.aborted)
    throw new DOMException("Obsolete membership settings context", "AbortError")

  if (!binding.api) return []

  const items: MembershipSettings[] = []
  const seen = new Set<string>()
  let after: string | null = null
  try {
    do {
      const page = await binding.api.list(after, signal)
      for (const row of page.items) {
        if (
          seen.has(row.membership_id) ||
          !admitsMembershipSettings(binding.session, "read", row.membership_id)
        )
          throw new TypeError("Unexpected membership settings scope")

        const scope = binding.session.context

        if (
          scope.kind === "organization" &&
          row.membership_id === scope.membership_id &&
          row.user_id !== (scope.delegation?.represented_user_id ?? binding.session.user.id)
        )
          throw new TypeError("Unexpected membership settings subject")

        seen.add(row.membership_id)
        items.push(row)
      }
      after = page.next_after_membership_id
    } while (after)

    return items
  } catch (error) {
    if (
      binding.current() &&
      [401, 403, 409].includes((error as { statusCode?: number })?.statusCode ?? 0)
    )
      rejectControlContext()

    throw error
  }
}
